import { roleIndicatesSupervisorNurse, type RoleContext } from './roles';
import { resolveCanonicalCategoryId } from './canonicalIds';

/** SN / SUPERVISOR_NURSE (+ legacy SUPERVISOR line). Used with NN team token per reporting-role-scope precedence. */
function contextIndicatesSupervisorNurse(roleContext: RoleContext | null | undefined): boolean {
  if (!roleContext) return false;
  if (roleIndicatesSupervisorNurse(roleContext.generalRole)) return true;
  if (roleIndicatesSupervisorNurse(roleContext.positionRole)) return true;
  if (roleIndicatesSupervisorNurse(roleContext.roleId)) return true;
  for (const r of roleContext.roles ?? []) {
    if (roleIndicatesSupervisorNurse(r)) return true;
  }
  return false;
}

/**
 * Team roles that widen reporting to all departments for a single compliance category.
 * Align with TEAM_ROLE_CATEGORY_IDS in roleResolver (NN → nurse_notes, ETHIC → ethicSurveys, RM → needleandsharp, PPP → siren_bundle).
 */
const HOSPITAL_WIDE_TEAM_CATEGORY: ReadonlyArray<{ token: string; categoryId: string }> = [
  { token: 'NN', categoryId: 'nurse_notes' },
  { token: 'ETHIC', categoryId: 'ethicSurveys' },
  { token: 'MED_ERROR', categoryId: 'med_error' },
  { token: 'RM', categoryId: 'needleandsharp' },
  { token: 'PPP', categoryId: 'siren_bundle' },
];

/** Uppercase team_role tokens from RoleContext (teamRole + teamRoles rows). */
export function collectTeamRoleTokensUpper(roleContext: RoleContext | null | undefined): string[] {
  if (!roleContext) return [];
  const raw = [
    roleContext.teamRole,
    roleContext.primaryTeamId,
    roleContext.primaryTeamCode,
    ...(roleContext.teamRoles ?? []),
    ...(roleContext.teamIds ?? []),
  ].filter(Boolean);
  const tokens = raw.map((r) => {
    if (typeof r === 'string') return r.toUpperCase();
    const o = r as { role?: string; teamCode?: string; teamId?: string; id?: string };
    return String(o.role ?? o.teamCode ?? o.teamId ?? o.id ?? '').toUpperCase();
  }).filter(Boolean);
  return Array.from(new Set(tokens));
}

/**
 * True when the user has the team_role that maps to this category (NN / ETHIC) and
 * teamAllowedCategoryIds includes that category (from Firestore / claims sync).
 * Used for dashboard, table, fetch: same rule on /report and /team — no separate "team page only" widening.
 */
export function hasTeamCategoryHospitalWideAccess(
  roleContext: RoleContext | null | undefined,
  categoryInput: string | undefined,
): boolean {
  if (!roleContext || !categoryInput?.trim()) return false;
  const canonical = resolveCanonicalCategoryId(categoryInput.trim()) ?? categoryInput.trim();
  const tokens = collectTeamRoleTokensUpper(roleContext);
  const allowed = (roleContext.teamAllowedCategoryIds ?? []).filter(Boolean);
  const supervisorNurse = contextIndicatesSupervisorNurse(roleContext);
  for (const { token, categoryId } of HOSPITAL_WIDE_TEAM_CATEGORY) {
    if (canonical !== categoryId) continue;
    if (!tokens.includes(token)) continue;
    // NN + supervisor nurse: widen nurse_notes even when teamAllowedCategoryIds omits it (e.g. ETHIC-only slice synced).
    // Other users still require the category gate below when `allowed` is non-empty.
    if (categoryId === 'nurse_notes' && token === 'NN' && supervisorNurse) {
      return true;
    }
    if (allowed.length > 0 && !allowed.includes(categoryId)) continue;
    return true;
  }
  return false;
}
